import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
    BarChart3, Activity, TrendingUp, Timer, PhoneCall, Eye,
    Users, Calendar, Globe, ArrowUp, ArrowDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';
import api from '../../lib/api';

export default function AnalyticsTab({ period }) {
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    const periodDays = period === 'week' ? 7 : period === 'month' ? 30 : period === 'quarter' ? 90 : 365;

    const fetchMetrics = useCallback(async () => {
        setLoading(true);
        try { const res = await api.get(`/internal/advanced-metrics?period=${periodDays}`); setMetrics(res.data); }
        catch { toast.error('Erreur lors du chargement des métriques'); }
        finally { setLoading(false); }
    }, [periodDays]);

    useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

    if (loading) return <div className="flex items-center justify-center py-12"><RefreshCw className="w-6 h-6 text-purple-400 animate-spin" /></div>;
    if (!metrics) return <div className="text-center py-12"><BarChart3 className="w-12 h-12 text-white/20 mx-auto mb-4" /><p className="text-white/60">Données non disponibles</p></div>;

    const maxDailyCount = Math.max(...metrics.daily_trend.map(d => d.count), 1);

    return (
        <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-slate-900/50 border-white/10"><CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-white/60 text-xs">Leads (période)</span>
                        <span className={`flex items-center text-xs font-medium ${metrics.period_comparison.growth.requests >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {metrics.period_comparison.growth.requests >= 0 ? <ArrowUp size={12} /> : <ArrowDown size={12} />}{Math.abs(metrics.period_comparison.growth.requests)}%
                        </span>
                    </div>
                    <p className="text-2xl font-bold text-white">{metrics.period_comparison.current.requests}</p>
                    <p className="text-xs text-white/40">vs {metrics.period_comparison.previous.requests} période précédente</p>
                </CardContent></Card>
                <Card className="bg-slate-900/50 border-white/10"><CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2"><Timer className="w-4 h-4 text-orange-400" /><span className="text-white/60 text-xs">Temps moyen conversion</span></div>
                    <p className="text-2xl font-bold text-white">{metrics.conversion_time.avg_days !== null ? `${metrics.conversion_time.avg_days}j` : '-'}</p>
                    <p className="text-xs text-white/40">Min: {metrics.conversion_time.min_days || '-'}j / Max: {metrics.conversion_time.max_days || '-'}j</p>
                </CardContent></Card>
                <Card className="bg-slate-900/50 border-white/10"><CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2"><PhoneCall className="w-4 h-4 text-blue-400" /><span className="text-white/60 text-xs">Taux de contact</span></div>
                    <p className="text-2xl font-bold text-blue-400">{metrics.funnel.contact_rate || 0}%</p>
                    <p className="text-xs text-white/40">Leads contactés / Total</p>
                </CardContent></Card>
                <Card className="bg-slate-900/50 border-white/10"><CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2"><Eye className="w-4 h-4 text-purple-400" /><span className="text-white/60 text-xs">Taux de démo</span></div>
                    <p className="text-2xl font-bold text-purple-400">{metrics.funnel.demo_rate || 0}%</p>
                    <p className="text-xs text-white/40">Contactés -> Démo</p>
                </CardContent></Card>
            </div>

            {/* Daily Trend */}
            <Card className="bg-slate-900/50 border-white/10">
                <CardHeader className="pb-2"><CardTitle className="text-white text-base flex items-center gap-2"><Activity className="w-4 h-4 text-purple-400" />Évolution des demandes ({periodDays} derniers jours)</CardTitle></CardHeader>
                <CardContent>
                    <div className="h-40 flex items-end gap-1">
                        {metrics.daily_trend.map((day, idx) => (
                            <div key={idx} className="flex-1 flex flex-col items-center gap-1 group">
                                <div className="relative w-full">
                                    <motion.div initial={{ height: 0 }} animate={{ height: `${Math.max((day.count / maxDailyCount) * 100, 3)}%` }} transition={{ delay: idx * 0.02 }}
                                        className="w-full bg-gradient-to-t from-purple-500 to-purple-400 rounded-t min-h-[4px] hover:from-purple-400 cursor-pointer"
                                        style={{ height: `${Math.max((day.count / maxDailyCount) * 100, 3)}%` }} />
                                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-slate-800 rounded text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                        {day.count} demande{day.count > 1 ? 's' : ''}<br /><span className="text-white/60">{new Date(day.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="flex justify-between mt-2 text-xs text-white/40">
                        <span>{new Date(metrics.daily_trend[0]?.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}</span>
                        <span>{new Date(metrics.daily_trend[metrics.daily_trend.length - 1]?.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}</span>
                    </div>
                </CardContent>
            </Card>

            {/* Funnel & Size Analysis */}
            <div className="grid lg:grid-cols-2 gap-4">
                <Card className="bg-slate-900/50 border-white/10">
                    <CardHeader className="pb-2"><CardTitle className="text-white text-base flex items-center gap-2"><TrendingUp className="w-4 h-4 text-green-400" />Entonnoir de conversion</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                        {[
                            { label: 'Total Leads', count: metrics.funnel.total_leads, color: 'bg-blue-500', width: 100 },
                            { label: 'Contactés', count: metrics.funnel.contacted + metrics.funnel.demo_scheduled + metrics.funnel.converted + metrics.funnel.lost, color: 'bg-yellow-500', width: metrics.funnel.contact_rate || 0 },
                            { label: 'Démo planifiée', count: metrics.funnel.demo_scheduled + metrics.funnel.converted, color: 'bg-purple-500', width: (metrics.funnel.demo_scheduled + metrics.funnel.converted) / metrics.funnel.total_leads * 100 || 0 },
                            { label: 'Convertis', count: metrics.funnel.converted, color: 'bg-green-500', width: metrics.funnel.converted / metrics.funnel.total_leads * 100 || 0 }
                        ].map((stage, idx) => (
                            <div key={idx}>
                                <div className="flex justify-between text-sm mb-1"><span className="text-white/80">{stage.label}</span><span className="text-white font-medium">{stage.count}</span></div>
                                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                    <motion.div initial={{ width: 0 }} animate={{ width: `${stage.width}%` }} transition={{ delay: idx * 0.1, duration: 0.5 }} className={`h-full ${stage.color} rounded-full`} />
                                </div>
                            </div>
                        ))}
                        <div className="pt-2 border-t border-white/10 flex justify-between text-sm">
                            <span className="text-white/60">Taux de closing</span><span className="text-green-400 font-bold">{metrics.funnel.close_rate || 0}%</span>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-slate-900/50 border-white/10">
                    <CardHeader className="pb-2"><CardTitle className="text-white text-base flex items-center gap-2"><Users className="w-4 h-4 text-blue-400" />Performance par taille d'équipe</CardTitle></CardHeader>
                    <CardContent>
                        {metrics.size_analysis.length === 0 ? <p className="text-white/40 text-sm text-center py-4">Pas assez de données</p> : (
                            <div className="space-y-2">
                                {metrics.size_analysis.slice(0, 5).map((size, idx) => (
                                    <div key={idx} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center"><Users className="w-4 h-4 text-white/60" /></div>
                                            <div><p className="text-white text-sm font-medium">{size.size} agents</p><p className="text-white/40 text-xs">{size.total} demandes</p></div>
                                        </div>
                                        <div className="text-right">
                                            <p className={`text-sm font-bold ${size.conversion_rate >= 50 ? 'text-green-400' : size.conversion_rate >= 25 ? 'text-yellow-400' : 'text-white/60'}`}>{size.conversion_rate}%</p>
                                            <p className="text-white/40 text-xs">{size.converted} conv.</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Day Distribution & Sources */}
            <div className="grid lg:grid-cols-2 gap-4">
                <Card className="bg-slate-900/50 border-white/10">
                    <CardHeader className="pb-2"><CardTitle className="text-white text-base flex items-center gap-2"><Calendar className="w-4 h-4 text-orange-400" />Distribution par jour</CardTitle></CardHeader>
                    <CardContent>
                        <div className="flex items-end justify-between h-24 gap-2">
                            {metrics.day_distribution.map((day, idx) => {
                                const maxCount = Math.max(...metrics.day_distribution.map(d => d.count), 1);
                                return (
                                    <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                                        <span className="text-xs text-white/60">{day.count}</span>
                                        <div className={`w-full rounded-t ${idx >= 1 && idx <= 5 ? 'bg-orange-400' : 'bg-orange-400/50'}`} style={{ height: `${Math.max((day.count / maxCount) * 100, 8)}%` }} />
                                        <span className="text-xs text-white/40">{day.day}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-slate-900/50 border-white/10">
                    <CardHeader className="pb-2"><CardTitle className="text-white text-base flex items-center gap-2"><Globe className="w-4 h-4 text-cyan-400" />Sources des leads</CardTitle></CardHeader>
                    <CardContent>
                        {metrics.source_analysis.length === 0 ? <p className="text-white/40 text-sm text-center py-4">Pas assez de données</p> : (
                            <div className="space-y-2">
                                {metrics.source_analysis.slice(0, 4).map((source, idx) => (
                                    <div key={idx} className="flex items-center justify-between py-2">
                                        <div className="flex items-center gap-2">
                                            <div className={`w-2 h-2 rounded-full ${source.source === 'landing_page' ? 'bg-purple-500' : source.source === 'referral' ? 'bg-green-400' : source.source === 'organic' ? 'bg-blue-400' : 'bg-gray-400'}`} />
                                            <span className="text-white/80 text-sm capitalize">{source.source === 'landing_page' ? 'Landing Page' : source.source === 'direct' ? 'Direct' : source.source}</span>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <span className="text-white font-medium text-sm">{source.count}</span>
                                            <span className={`text-xs font-medium px-2 py-0.5 rounded ${source.conversion_rate >= 50 ? 'bg-green-500/20 text-green-400' : source.conversion_rate >= 25 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-white/10 text-white/60'}`}>{source.conversion_rate}%</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
